import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Check } from "lucide-react";
import axios from "axios";
import { SEO, buildProductJsonLd } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";
import { HeroImage } from "@/components/HeroImage";
import { EnquiryForm } from "@/components/EnquiryForm";
import { BuyNowButton } from "@/components/BuyNowButton";
import { AddToCartButton } from "@/components/AddToCartButton";
import { ProductOptions } from "@/components/ProductOptions";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { RecentlyViewedStrip } from "@/components/RecentlyViewedStrip";
import { ProductGallery, normalizeGalleryImage } from "@/components/ProductGallery";
import { BushCabinPresoldModal } from "@/components/BushCabinPresoldModal";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => n?.toLocaleString("en-NZ");

export default function BushCabinDetailPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  // Reached via "View Details" from the builder — browse-only: no price/Buy
  // Now/Add to Cart or repeated marketing blocks, just the photos/detail,
  // with a clear way back to pick up the build where they left it.
  const fromBuild = searchParams.get("from") === "build";
  const [models, setModels] = useState(null); // null = still loading

  useEffect(() => {
    axios.get(`${API}/api/products/bushcabin`).then((res) => setModels(res.data)).catch((err) => { console.error(err); setModels([]); });
  }, []);

  const model = models?.find((m) => m.slug === slug);
  const hasOptions = model?.optionGroups?.length > 0;
  const [optionsState, setOptionsState] = useState({ selectedOptions: {}, totalPrice: model?.price || 0, allRequiredSelected: !hasOptions, combinationInStock: true });
  const recentlyViewed = useRecentlyViewed(
    model && { path: `/bush-cabin/${model.slug}`, name: model.name, thumb: model.thumb, price: model.price, priceFrom: model.priceFrom, priceOnRequest: model.priceOnRequest }
  );

  if (models === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }
  if (!model) return <Navigate to="/bush-cabin" replace />;

  const canBuyNow = !model.priceOnRequest && model.inStock !== false;
  const related = models.filter((m) => m.slug !== model.slug).slice(0, 3);
  const gallery = model.gallery && model.gallery.length > 0 ? model.gallery.map(normalizeGalleryImage) : [{ src: model.thumb, alt: null, type: "image", pos: "center" }];

  return (
    <div data-testid="bushcabin-detail-page">
      <SEO
        path={`/bush-cabin/${model.slug}`}
        title={`${model.name} · Bushtech NZ`}
        description={`${model.name} — ${model.tagline}. ${model.intro.slice(0, 140)}`}
        image={model.hero || model.thumb}
        jsonLd={buildProductJsonLd({ name: model.name, description: model.tagline, image: model.hero || model.thumb, price: model.price, priceOnRequest: model.priceOnRequest, inStock: model.inStock, url: `/bush-cabin/${model.slug}` })}
      />

      <section className="relative min-h-[60svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <HeroImage src={model.hero || model.thumb} alt={model.name} className="w-full h-full" desktopPosition={model.heroPosition || "50% center"} mobilePosition={model.heroFocalMobile} />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-8% via-black/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-16 pt-32 w-full">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <Link to="/bush-cabin" data-testid="bushcabin-detail-breadcrumb" className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 hover:text-[var(--brand-primary)]">
              ← Bush Cabin
            </Link>
            {model.badge && (
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">
                {model.badge}
              </span>
            )}
          </div>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.9] text-white hero-text-shadow max-w-4xl">
            {model.name.toUpperCase()}
          </h1>
          <p className="text-base md:text-lg text-[var(--brand-primary)] font-mono uppercase tracking-widest mt-5">
            {model.tagline}
          </p>
          <div className="mt-8 flex items-start gap-6 flex-wrap">
            {fromBuild ? (
              <Link to="/build-your-bush-cabin" data-testid="bushcabin-detail-back-to-builder" className="btn-primary">
                <ArrowLeft size={14} /> Back To Builder
              </Link>
            ) : (
              <>
                <div>
                  <p className="font-display text-4xl md:text-5xl text-white leading-none">
                    {model.priceOnRequest ? (
                      <span className="text-2xl font-mono uppercase tracking-widest text-white/80">Enquire For Price</span>
                    ) : (
                      <>
                        {(model.priceFrom && !hasOptions) && <span className="text-lg font-mono uppercase tracking-widest text-white/70 mr-2">FROM</span>}
                        ${fmt(hasOptions ? optionsState.totalPrice : model.price)}
                      </>
                    )}
                  </p>
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 mt-2">
                    {model.priceOnRequest ? "" : "GST included · "}Availability: {model.availability}
                  </p>
                </div>
                <div className="flex items-start gap-3 flex-wrap">
                  {canBuyNow && (
                    <>
                      <BuyNowButton
                        productPath={`/bush-cabin/${model.slug}`}
                        testId="bushcabin-detail-cta-buy-now"
                        selectedOptions={hasOptions ? optionsState.selectedOptions : null}
                        disabled={hasOptions && (!optionsState.allRequiredSelected || !optionsState.combinationInStock)}
                        disabledHint={hasOptions && optionsState.allRequiredSelected && !optionsState.combinationInStock ? "" : "Select all options below to continue"}
                      />
                      <AddToCartButton
                        productPath={`/bush-cabin/${model.slug}`}
                        testId="bushcabin-detail-cta-add-to-cart"
                        name={model.name}
                        thumb={model.thumb}
                        unitPrice={hasOptions ? optionsState.totalPrice : model.price}
                        priceFrom={model.priceFrom && !hasOptions}
                        selectedOptions={hasOptions ? optionsState.selectedOptions : null}
                        disabled={hasOptions && (!optionsState.allRequiredSelected || !optionsState.combinationInStock)}
                      />
                    </>
                  )}
                  <a href="#enquire" data-testid="bushcabin-detail-cta-enquire" className="btn-secondary">Enquire About This <ArrowUpRight size={14} /></a>
                </div>
              </>
            )}
          </div>
          {hasOptions && !fromBuild && (
            <div className="mt-8 max-w-lg">
              <ProductOptions optionGroups={model.optionGroups} variantStock={model.variantStock || []} basePrice={model.price} onChange={setOptionsState} />
            </div>
          )}
        </div>
      </section>

      {/* AUSTRALIAN SHIPPING CALLOUT */}
      {!fromBuild && (
        <div data-testid="bushcabin-detail-au-shipping-banner" className="bg-[var(--brand-primary)] py-3 md:py-3.5">
          <p className="container-x text-center font-mono text-[11px] md:text-sm tracking-[0.15em] uppercase text-black font-semibold">
            Australian Customers —{" "}
            <a href="#enquire" data-testid="bushcabin-detail-au-shipping-link" className="underline hover:no-underline">
              Contact us for a custom freight quote
            </a>
          </p>
        </div>
      )}

      <section className="container-x py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="order-1 lg:col-span-7 lg:row-start-1">
            <ProductGallery gallery={gallery} productName={model.name} />
          </div>
          <div className="order-2 lg:col-span-5 lg:row-start-1 lg:row-span-2 space-y-10">
            <div>
              <p className="overline mb-4">— About This Configuration</p>
              <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed whitespace-pre-line">{model.intro}</p>
            </div>
            <div className="border-t border-[var(--border-default)] pt-8">
              <p className="overline mb-5">— Key Features</p>
              <ul className="space-y-3">
                {model.features.map((f, i) => (
                  <li key={f || i} className="flex items-start gap-3 text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
                    <Check size={16} className="text-[var(--brand-primary)] mt-1 shrink-0" strokeWidth={2.5} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="order-3 lg:col-span-7 lg:row-start-2 space-y-10">
            {!fromBuild && (
              <>
              <div className="border border-[var(--border-default)] bg-[var(--bg-secondary)] overflow-hidden" data-testid="bushcabin-detail-self-contained-section">
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="p-8 md:p-10 flex flex-col justify-center order-2 md:order-1">
                  <p className="overline mb-3">— Self-Contained Options</p>
                  <h3 data-testid="bushcabin-detail-self-contained-heading" className="font-display text-2xl md:text-3xl text-white leading-tight mb-4">
                    Need A Fully Certified Self-Contained Bush Cabin Build?
                  </h3>
                  <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
                    Talk to us about the options. Some customers request a fully certified self-contained build — others don't need one.
                  </p>
                  <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed mt-4">
                    For those who do, we're working with one of NZ's premier van interior fitout firms to deliver interior fitout options suited to your needs.
                  </p>
                  <a href="#enquire" data-testid="bushcabin-detail-self-contained-cta" className="btn-secondary mt-8 self-start">
                    Talk To Us About The Options
                  </a>
                </div>
                <div className="relative order-1 md:order-2 bg-[#F5F0E8] flex items-center justify-center p-6 md:p-10">
                  <img
                    src="/products/bushcabin/proof/self-contained-layout.png"
                    alt="Example self-contained interior layout options for the Bush Cabin"
                    className="w-full h-auto max-w-md"
                  />
                </div>
              </div>
              <div className="border-t border-[var(--border-default)] p-8 md:p-10" data-testid="bushcabin-detail-vehicle-acquisition-block">
                <h4 className="font-display text-xl md:text-2xl text-white leading-tight mb-3">
                  Don't Have The Vehicle Yet?
                </h4>
                <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
                  We can assist you — from advising on acquiring a used or new vehicle, through everything needed to start with a stock standard vehicle and end up with a fully equipped Bush Cabin camper build.
                </p>
                <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed mt-4">
                  We recommend getting in touch with us sooner rather than later, as it can take time to properly plan out a build like this in time for your intended travels.
                </p>
              </div>
              <div className="border-t border-[var(--border-default)] p-8 md:p-10" data-testid="bushcabin-detail-accessories-callout">
                <h4 className="font-display text-xl md:text-2xl text-white leading-tight mb-3">
                  Already Own A Bush Cabin?
                </h4>
                <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
                  MOLLE panels, drop-down tables, cabin storage and more are all available separately — add them to your Bush Cabin any time.
                </p>
                <Link to="/bush-cabin#accessories" data-testid="bushcabin-detail-accessories-callout-link" className="btn-secondary mt-6 inline-flex">
                  Shop Bush Cabin Accessories <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>

            <div className="border border-[var(--border-default)] bg-[var(--bg-secondary)] overflow-hidden grid grid-cols-1 md:grid-cols-2" data-testid="bushcabin-detail-xseries-callout">
              <div className="relative aspect-[4/3] md:aspect-auto overflow-hidden">
                <img src="/products/bushcabin/proof/xseries-callout.jpg" alt="Bushtech X-Series Tray Canopy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
                <p className="absolute bottom-4 left-4 right-4 font-mono text-[10px] tracking-[0.2em] uppercase text-white/90 leading-relaxed">
                  Our Bushtech X-Series Demonstrator
                </p>
              </div>
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <p className="overline mb-3">— Engineering Pedigree</p>
                <h3 className="font-display text-2xl md:text-3xl text-white leading-tight mb-4">Built By Bushtech</h3>
                <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
                  Bushtech is best known internationally for its commercial-duty aluminium canopies, proven on 4WDs in some of the harshest environments in the world.
                </p>
                <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed mt-4">
                  Its aluminium ute canopy carries a 300kg dynamic load — 50% greater than its nearest competitor — while weighing less than even plastic and fibreglass canopies.
                </p>
                <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed mt-4">
                  The Bush Cabin comes from the same engineering team behind our formidable X-Series builds. Serious 4WD engineering is what Bushtech does.
                </p>
              </div>
            </div>
              </>
            )}
          </div>
        </div>
      </section>

      {model.contentSections && model.contentSections.length > 0 && (
        <section className="border-t border-[var(--border-default)] bg-[var(--bg-secondary)]">
          <div className="container-x py-16 md:py-20">
            <div className="mb-12 max-w-2xl">
              <p className="overline mb-4">— More About This Configuration</p>
              <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
                BUILT TO A <span className="text-[var(--brand-primary)]">DIFFERENT STANDARD.</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-12">
              {model.contentSections.map((section, i) => (
                <div key={section.heading || i} data-testid={`content-section-${i}`} className="border-t border-[var(--border-default)] pt-6">
                  {section.heading && (
                    <h3 className="font-display text-xl md:text-2xl text-white mb-3">{section.heading}</h3>
                  )}
                  {section.body && (
                    <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed whitespace-pre-line">{section.body}</p>
                  )}
                  {section.bullets && section.bullets.length > 0 && (
                    <ul className="mt-3 space-y-2">
                      {section.bullets.map((b, bi) => (
                        <li key={b || bi} className="flex items-start gap-2 text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
                          <Check size={14} className="text-[var(--brand-primary)] mt-1 shrink-0" strokeWidth={2.5} />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {!fromBuild && related.length > 0 && (
        <section className="container-x pb-16 md:pb-20 border-t border-[var(--border-default)] pt-16 md:pt-20">
          <div className="mb-8">
            <p className="overline mb-3">— Other Configurations</p>
            <h2 className="font-display text-3xl md:text-4xl text-white">
              MORE <span className="text-[var(--brand-primary)]">BUSH CABIN.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {related.map((r, i) => (
              <Link key={r.slug} to={`/bush-cabin/${r.slug}`} data-testid={`related-bushcabin-${i}`} className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors">
                <div className="aspect-[4/3] overflow-hidden relative">
                  <HoverImage primary={r.thumb} secondary={r.gallery?.[1]?.src} alt={r.name} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="font-display text-xl md:text-2xl text-white leading-tight">{r.name}</h3>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-[var(--brand-primary)] mt-2">
                      {r.priceOnRequest ? "Enquire" : `${r.priceFrom ? "FROM " : ""}$${fmt(r.price)}`}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {!fromBuild && <RecentlyViewedStrip items={recentlyViewed} />}

      {fromBuild ? (
        <section className="border-t border-[var(--border-default)] bg-[var(--bg-secondary)]">
          <div className="container-x py-16 md:py-20 text-center">
            <p className="text-[var(--text-secondary)] text-base md:text-lg mb-6">Like what you see? Head back and add it to your build.</p>
            <Link to="/build-your-bush-cabin" data-testid="bushcabin-detail-back-to-builder-bottom" className="btn-primary inline-flex">
              <ArrowLeft size={14} /> Back To Builder
            </Link>
          </div>
        </section>
      ) : (
        <section id="enquire" className="border-t border-[var(--border-default)] bg-[var(--bg-secondary)] scroll-mt-24">
          <div className="container-x py-16 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5">
                <p className="overline mb-4">— Register Interest</p>
                <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
                  ASK US<br /><span className="text-[var(--brand-primary)]">ANYTHING.</span>
                </h2>
                <p className="text-[var(--text-secondary)] text-base md:text-lg mt-6 leading-relaxed">
                  Vehicle compatibility, current stock, viewing in Christchurch, freight to your region — get proper answers.
                </p>
              </div>
              <div className="lg:col-span-7">
                <div className="border border-[var(--border-default)] bg-[var(--bg-main)] p-6 md:p-8">
                  <EnquiryForm defaultProduct={`Bush Cabin — ${model.name}`} />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      <BushCabinPresoldModal />
    </div>
  );
}
